import { useState } from "react";
import { Box, TextField, Grid, Button, Link, Alert } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { LanguageItem, AuthPageLayout } from "../../lib/common";
import AsyncFormBox from "../../lib/common/AsyncFormBox";
import { usePasswordValidation } from "../../lib/hooks";
import { CommonCalls } from "../../config/calls";

/**
 * Translates backend error codes into user-friendly messages for the reset password form.
 * @param {Error} error - Axios error from the API call.
 * @returns {React.ReactNode} Translated error message.
 */
function translateError(error) {
  const code = error.response?.data?.code;

  switch (code) {
    case "glint-js/mismatchingPasswords":
      return "Hesla se neshodují.";
    case "glint-js/invalidResetToken":
      return "Odkaz pro obnovení hesla je neplatný nebo vypršel.";
  }
}

function BackToSignInLink() {
  return (
    <Link component={RouterLink} to={"/signIn"} variant={"body2"}>
      <LanguageItem cs>Zpět na přihlášení</LanguageItem>
      {/* <LanguageItem en>Back to Sign In</LanguageItem> */}
    </Link>
  );
}

function InvalidTokenView() {
  return (
    <AuthPageLayout
      title={
        <>
          <LanguageItem cs>Neplatný odkaz</LanguageItem>
          {/* <LanguageItem en>Invalid Link</LanguageItem> */}
        </>
      }>
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <LanguageItem cs>Odkaz pro obnovení hesla je neplatný. Zkuste požádat o nový.</LanguageItem>
          {/* <LanguageItem en>The password reset link is invalid. Please request a new one.</LanguageItem> */}
        </Alert>
        <Link component={RouterLink} to={"/forgotPassword"} variant={"body2"}>
          <LanguageItem cs>Požádat o nový odkaz</LanguageItem>
          {/* <LanguageItem en>Request a new link</LanguageItem> */}
        </Link>
      </Box>
    </AuthPageLayout>
  );
}

function SuccessView() {
  return (
    <Box sx={{ mt: 3, maxWidth: 400, textAlign: "center" }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        <LanguageItem cs>Vaše heslo bylo úspěšně změněno.</LanguageItem>
        {/* <LanguageItem en>Your password has been changed successfully.</LanguageItem> */}
      </Alert>
      <BackToSignInLink />
    </Box>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [submitted, setSubmitted] = useState(false);
  const { validate, passwordFieldProps, confirmPasswordFieldProps } = usePasswordValidation();

  if (!token) {
    return <InvalidTokenView />;
  }

  /**
   * Submits the new password to the backend.
   * @param {object} values - Form values containing password and confirmPassword.
   * @returns {Promise} API call promise.
   */
  function handleSubmit(values) {
    return CommonCalls.changePasswordByReset({
      token,
      password: values.password,
      confirmPassword: values.confirmPassword,
    });
  }

  /**
   * Handles successful password change by showing a confirmation message.
   */
  function onSubmitDone() {
    setSubmitted(true);
  }

  /**
   * Validates password requirements and confirmation match.
   * @param {object} values - Form values.
   * @returns {boolean} True if validation passes.
   */
  function validateInput(values) {
    return validate(values.password, values.confirmPassword);
  }

  return (
    <AuthPageLayout
      title={
        <>
          <LanguageItem cs>Nastavení nového hesla</LanguageItem>
          {/* <LanguageItem en>Set New Password</LanguageItem> */}
        </>
      }>
      {submitted && <SuccessView />}

      {!submitted && (
        <AsyncFormBox
          onValidate={validateInput}
          onSubmit={handleSubmit}
          onSubmitDone={onSubmitDone}
          translateError={translateError}
          boxProps={{
            sx: { mt: 2 },
          }}>
          <TextField
            {...passwordFieldProps}
            margin={"normal"}
            required
            fullWidth
            name={"password"}
            label={
              <>
                <LanguageItem cs>Nové heslo</LanguageItem>
                {/* <LanguageItem en>New Password</LanguageItem> */}
              </>
            }
            type={"password"}
            autoComplete={"new-password"}
            autoFocus
          />
          <TextField
            {...confirmPasswordFieldProps}
            margin={"normal"}
            required
            fullWidth
            name={"confirmPassword"}
            label={
              <>
                <LanguageItem cs>Potvrďte nové heslo</LanguageItem>
                {/* <LanguageItem en>Confirm New Password</LanguageItem> */}
              </>
            }
            type={"password"}
            autoComplete={"new-password"}
          />

          <Button type={"submit"} fullWidth variant={"contained"} sx={{ mt: 3, mb: 2 }}>
            <LanguageItem cs>Změnit heslo</LanguageItem>
            {/* <LanguageItem en>Change Password</LanguageItem> */}
          </Button>

          <Grid container justifyContent={"flex-end"}>
            <Grid>
              <BackToSignInLink />
            </Grid>
          </Grid>
        </AsyncFormBox>
      )}
    </AuthPageLayout>
  );
}

export default ResetPassword;
